import React, { ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { TextParam } from '../../types/TextParam';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { Label } from '../Label/Label';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { UrlParam } from '../../types/UrlParam';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValidateTextLength } from '../../utils/useValidateTextLength';
import { isNullOrUndefined } from '../../../../lib/typeGuards';
import { FieldWrapperModifier } from '../../grid/FieldWrapper/FieldWrapper';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type ValidInputParams = TextParam<string> | UrlParam<string>;

type PrefillableTextInput = {
  label: ReactNode;
  helpText?: string;
  tooltipPlacement?: Label['tooltipPlacement'];
  param?: ValidInputParams;
  buttonLabel: string;
  preparedValue: string | undefined;
  value?: string | undefined;
  ref?: React.RefObject<HTMLInputElement>;
} & Omit<HTMLInputProps, 'id' | 'ref' | 'className' | 'value'>;

/**
 * Normalize constraints and values from backend, which are not html conform,
 * to html conform input values.
 */
function normalizeParamsToHTMLAttributes(inputProps: HTMLInputProps, value: string, param?: ValidInputParams) {
  return {
    type: param?.type,
    required: param?.required,
    maxLength: param?.constraints?.maxLength,
    readOnly: param?.constraints?.readonly,
    pattern: param?.constraints?.pattern,
    name: param?.name,
    ...inputProps,
    value: value,
  };
}

export function PrefillableTextInput({
  label,
  helpText,
  tooltipPlacement,
  param,
  buttonLabel,
  preparedValue,
  ref,
  ...inputProps
}: PrefillableTextInput) {
  const { t } = useSonTranslation('Form');

  const [value, setValue] = useState(param?.value || inputProps.value || '');

  const inputRef = useRef<HTMLInputElement>(null);
  // expose the inputRef from outside
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const id = useHtmlValidId();
  const helpTextId = `input-help-text-${id}`;
  const inputId = `input-${id}`;
  const htmlAttributes = normalizeParamsToHTMLAttributes(inputProps, value, param);

  useValidateTextLength(inputRef);

  // fields can not! only contain white spaces.
  // check for >= 1 for normal validation of fields
  const validateEmptySpaces = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { type, pattern } = htmlAttributes;
    if (type === 'text' && isNullOrUndefined(pattern) && e.target.value.length >= 1 && e.target.value.trim() === '') {
      e.target.setCustomValidity(t('fieldCantBeEmpty'));
    } else {
      e.target.setCustomValidity('');
    }
  };

  return (
    <ElementContainer>
      <Label
        htmlFor={inputId}
        helpText={helpText}
        helpTextId={helpTextId}
        tooltipPlacement={tooltipPlacement}
        required={htmlAttributes?.required}
      >
        {label}
      </Label>
      <FieldWrapper
        // no inputCounter on readonly inputs
        maxLength={htmlAttributes.readOnly ? undefined : htmlAttributes.maxLength}
        elementRef={inputRef}
        modifier={FieldWrapperModifier.PREFILLABLE_TEXT_INPUT}
      >
        {preparedValue ? (
          <button
            type="button"
            className="prefillable-text-input__button"
            aria-controls={inputId}
            aria-disabled={!preparedValue}
            onClick={() => {
              setValue(preparedValue || '');
            }}
          >
            {buttonLabel}
          </button>
        ) : null}
        <input
          {...htmlAttributes}
          ref={inputRef}
          id={inputId}
          aria-describedby={helpText && helpTextId}
          className="form2-input"
          onChange={e => {
            setValue(e.target.value);
            validateEmptySpaces(e);
          }}
          value={value}
        />
      </FieldWrapper>
    </ElementContainer>
  );
}
